import { Schema } from "@generation/core";
import {
  Bestand,
  Contactgegevens,
  CreateKlantcontactCommand,
  Formulier,
  FormulierSectie,
  FormulierVraag,
  Persoonsgegevens
} from "@klantondersteuning/client";
import { shallowFlatten } from "@shared/shared-generic-rekenmodule";
import { fileToBase64 } from "../../utils/file-to-upload";
import { RequestInformation } from "../interface/RequestInformation";
import {
  findFirstFieldInSteps,
  getDisplayNameOfSubmittedValue,
  getFieldNamesInSteps,
  getSectionOfFieldInSteps
} from "../utils/schemaHelper";
import { IsBijlageFieldAndHasAtleastOneFile } from "../utils/submittedFieldGuard";
import { isNumber, isText } from "../utils/typeGuards";
import { mapLanguageToTaal } from "./mapLanguageToTaal";
import { mapRegelingOptionToRegeling } from "./mapRegelingOptionToRegeling";

export interface FileListFieldInfo {
  fileList: FileList;
  fieldName: string;
}

export class SchemaToRequestMapper {
  private MapFormFieldToCommand(
    command: CreateKlantcontactCommand,
    fieldName: string,
    fieldValue: string,
    fieldLabel: string,
    fieldValueLabel: string,
    sectionName: string
  ) {
    const lowercaseFieldName = fieldName.toLowerCase();

    if (this.ContactgegevensFields.includes(lowercaseFieldName)) {
      this.MapContactGegevensField(command.contactgegevens, fieldName, fieldValue);
    } else if (this.PersoonsgegevensField.includes(lowercaseFieldName)) {
      this.MapPersoonsGegevensField(command.persoonsgegevens, fieldName, fieldValue);
    }

    if ("regeling" === lowercaseFieldName) {
      command.regeling = mapRegelingOptionToRegeling(fieldValue);
    }

    let sectionIndex = command.formulier.secties.findIndex((sectie) => sectie.titel === sectionName);
    if (sectionIndex === -1) {
      command.formulier.secties.push(
        new FormulierSectie({
          titel: sectionName,
          vragen: []
        })
      );
      sectionIndex = command.formulier.secties.length - 1;
    }

    // Include all fields in the form as they will be used to
    this.MapFormulierVraagField(command.formulier.secties[sectionIndex].vragen, fieldLabel, fieldValueLabel);
  }

  protected ContactgegevensFields: string[] = ["email"];
  protected MapContactGegevensField(contactgegevens: Contactgegevens, fieldName: string, fieldValue: string) {
    // The to lower case works because the model only has lower case properties
    // This fixes potential bugs where Email would not be mapped to the client model correctly.
    contactgegevens[fieldName.toLowerCase()] = fieldValue;
  }
  protected PersoonsgegevensField: string[] = [];
  protected MapPersoonsGegevensField(
    persoonsgegevens: Persoonsgegevens,
    fieldName: string,
    fieldValue: string
  ) {}

  map(
    flowSteps: Schema[],
    formulier: any,
    language: string,
    formulierKenmerk: string,
    onderwerp: string,
    requestInformation?: RequestInformation
  ) {
    return new Promise<CreateKlantcontactCommand>((resolve, reject) => {
      const command = new CreateKlantcontactCommand();
      command.contactgegevens = new Contactgegevens();
      command.persoonsgegevens = new Persoonsgegevens();
      command.formulier = new Formulier({
        secties: [],
        onderwerp: onderwerp,
        behandelaarsOnderwerp: undefined,
        sleutel: formulierKenmerk
      });
      command.taal = mapLanguageToTaal(language);

      const orderedFormFieldIds = getFieldNamesInSteps(flowSteps);
      const submittedFields = shallowFlatten(formulier);
      if (!submittedFields) {
        throw Error("Unable to retrieve submitted values");
      }

      command.formulier.behandelaarsOnderwerp = this.getBehandelaarsOnderwerp(submittedFields);

      // Map submitted string fields
      orderedFormFieldIds.forEach((submittedFieldName) => {
        const field = findFirstFieldInSteps(flowSteps, submittedFieldName);
        const sectionName = getSectionOfFieldInSteps(flowSteps, submittedFieldName);
        let submittedValue = submittedFields[submittedFieldName];
        if (submittedValue && isNumber(submittedValue)) {
          submittedValue = submittedValue.toLocaleString("nl-NL");
        }
        if (field && submittedValue && Array.isArray(submittedValue)) {
          const arrayValue = submittedValue.map(
            (item) => getDisplayNameOfSubmittedValue(field, item) ?? item
          );
          if (Array.isArray(arrayValue) && arrayValue.length > 0) {
            this.MapFormulierArrayField(
              command.formulier.secties[0].vragen,
              field.label || submittedFieldName,
              arrayValue
            );
          }
        }
        if (field && submittedValue && isText(submittedValue)) {
          const displayNameOfSubmittedValue = getDisplayNameOfSubmittedValue(field, submittedValue);
          this.MapFormFieldToCommand(
            command,
            submittedFieldName,
            submittedValue,
            field.label ?? submittedFieldName,
            displayNameOfSubmittedValue ?? submittedValue,
            sectionName
          );
        }
      });

      // Map bijlage fields
      const bijlageFields: FileListFieldInfo[] = Object.entries(submittedFields)
        .filter((entry) => {
          return IsBijlageFieldAndHasAtleastOneFile(entry[1]);
        })
        .map((entry) => {
          return { fileList: entry[1], fieldName: entry[0] };
        });
      this.mapBijlage(command, bijlageFields, resolve, reject);

      this.mapRequestInformationToCommand(command, requestInformation);
    });
  }

  private MapFormulierVraagField(
    formuliervragen: FormulierVraag[],
    fieldLabel: string,
    fieldValueLabel: string
  ) {
    const vraag: FormulierVraag = new FormulierVraag({
      vraag: fieldLabel,
      antwoord: fieldValueLabel
    });
    formuliervragen.push(vraag);
  }

  private MapFormulierArrayField(
    formuliervragen: FormulierVraag[],
    fieldLabel: string,
    fieldValueLabels: string[]
  ) {
    const vraag: FormulierVraag = new FormulierVraag({
      vraag: fieldLabel,
      antwoord: "-",
      antwoorden: fieldValueLabels
    });
    formuliervragen.push(vraag);
  }

  protected transformBijlage = (bijlage: File, _fieldName: string): Promise<File> => {
    return Promise.resolve(bijlage);
  };

  private async mapBijlage(
    command: CreateKlantcontactCommand,
    bijlageFields: FileListFieldInfo[],
    resolve,
    reject
  ) {
    await Promise.all(
      bijlageFields.map((bijlageField) => {
        return Promise.all(
          Array.from(bijlageField.fileList).map(async (file) => {
            const transformedFile = await this.transformBijlage(file, bijlageField.fieldName);
            const bijlage = new Bestand({
              base64Inhoud: await fileToBase64(transformedFile),
              bestandsnaam: transformedFile.name
            });

            if (!command.bijlagen) {
              command.bijlagen = [];
            }
            command.bijlagen.push(bijlage);
          })
        );
      })
    )
      .then(() => {
        resolve(command);
      })
      .catch((error) => {
        reject(error);
      });
  }

  private mapRequestInformationToCommand(
    command: CreateKlantcontactCommand,
    requestInformation?: RequestInformation
  ) {
    if (!requestInformation) {
      return;
    }

    if (!command.regeling && requestInformation.regeling) {
      command.regeling = requestInformation.regeling;
    }
  }

  protected getBehandelaarsOnderwerp(_formulier: unknown): string | undefined {
    return undefined;
  }
}
